import { GAME_MODE, Section, songBeat } from '~/interfaces';

import { PassTheMicUiArgs, PassTheMicUiState } from './types';

// Lives with the game state now - the score reads it too, and a route helper is the wrong place for
// something the engine depends on. Re-exported so the lyrics components keep one import.
export { getSingerAtSection } from '~/modules/game-engine/game-state/helpers/turns';

export const clamp = (min: number, value: number, max: number) => Math.max(min, Math.min(max, value));

/**
 * Whether this syllable is followed by a space.
 *
 * The token is drawn trimmed and the space put back as an `&nbsp;` INSIDE the same box, so adjacent
 * words sit edge to edge and every visible gap between them comes from here. A note whose trailing
 * whitespace is not a plain U+0020 - a no-break space, a thin space, whatever a chart was typed with
 * - used to fail this test, get trimmed, and have nothing put back, which runs the words together.
 * Three songs in the catalogue are typed that way.
 */
export const hasLyricTrailingSpace = (lyrics: string) => /\s$/.test(lyrics);

export const getHeadstartPercent = (
  currentBeat: songBeat,
  sectionStartBeat: songBeat,
  beatsBetweenSectionAndNote: number,
) => {
  if (beatsBetweenSectionAndNote <= 0) return 2;
  return clamp(0, (currentBeat - sectionStartBeat) / beatsBetweenSectionAndNote, 2);
};

export const getNoteFill = (currentBeat: songBeat, note: { start: songBeat; length: number }) => {
  return clamp(0, (currentBeat - note.start) / note.length, 2);
};

/**
 * A turn shorter than this trades lines rather than hands the microphone over: in a chorus that
 * changes every few seconds the warning would be on screen more often than not, and stops meaning
 * anything. Measured per turn, so one song can do both.
 */
export const TRADING_LINES_TURN_MS = 8_000;

/** How long before the change the hand-over is announced. */
export const HANDOVER_WARNING_MS = 2_500;

/**
 * A pause worth telling the room about. The same order as an intro worth skipping, but its own
 * constant: `SKIP_INTRO_MS` drops to a second under dev and e2e so a test can skip quickly, and
 * that must not turn every one-second gap between verses into an announcement.
 */
export const BREAK_ANNOUNCE_MS = 8_000;

/** The last stretch of a break, when it stops being a rest and starts being a cue. */
export const GET_READY_MS = 5_000;

export interface BreakState {
  /** As the band says it: seconds rounded up, so it never reads 0 while the break is still on. */
  secondsLeft: number;
  /** How much of the break is left, 0 to 1, for the draining bar. */
  progress: number;
  ready: boolean;
}

/**
 * A pause long enough that the screen should say so. Without it a 24-second instrumental renders as
 * an empty band and looks identical to a bug.
 */
export const getBreakState = ({
  section,
  currentBeat,
  beatLengthMs,
}: {
  section: Section | null;
  currentBeat: songBeat;
  beatLengthMs: number;
}): BreakState | null => {
  // Keyed off the pause rather than off `isNotesSection`, which is false for a notes section with
  // no notes in it - and only a pause carries the `end` this measures against.
  if (section?.type !== 'pause') return null;

  const totalMs = (section.end - section.start) * beatLengthMs;
  if (totalMs <= BREAK_ANNOUNCE_MS) return null;

  const msLeft = clamp(0, (section.end - currentBeat) * beatLengthMs, totalMs);

  return {
    secondsLeft: Math.ceil(msLeft / 1000),
    progress: msLeft / totalMs,
    ready: msLeft <= GET_READY_MS,
  };
};

const IDLE: PassTheMicUiState = {
  tradingLines: false,
  handingOver: false,
  handOverAtBreakEnd: false,
  progressPercent: 0,
  showSwapOnCurrentLine: false,
  showSwapOnNextLine: false,
  msToChange: Infinity,
  currentSinger: 0,
  nextSinger: 0,
};

export const getPassTheMicUiState = (args: PassTheMicUiArgs): PassTheMicUiState => {
  // Nobody to pass to: one singer on a track sings all of it, whatever the mode says. The old
  // blink and progress bar announced a hand-over to a lone singer for the whole song.
  if (args.mode !== GAME_MODE.PASS_THE_MIC || args.singersOnTrack < 2) {
    return IDLE;
  }

  const sectionStart = args.sectionStart ?? null;
  const changes = args.changes;

  const passed = changes.filter((beat) => (sectionStart === null ? false : beat <= sectionStart));

  const previousChange = Math.max(0, ...passed);
  const nextChange = changes.find((beat) => (sectionStart === null ? false : beat > sectionStart)) ?? Infinity;
  const msToChange = (nextChange - args.currentBeat) * args.beatLengthMs;
  const turnMs = (nextChange - previousChange) * args.beatLengthMs;

  // Nothing to hand over once the last change has passed, so that turn is neither trading nor warning.
  const tradingLines = Number.isFinite(turnMs) && turnMs < TRADING_LINES_TURN_MS;

  // One announcement rather than two stacked: a break that ends on a change already reads "Break
  // 18 s -> Tom", so the edge and the chip have nothing left to say.
  const handOverAtBreakEnd =
    args.sectionIsPause && args.nextSectionStart !== null && args.nextSectionStart === nextChange;
  const handingOver = !tradingLines && !handOverAtBreakEnd && msToChange > 0 && msToChange < HANDOVER_WARNING_MS;

  // The bar IS the countdown, so it drains across the warning rather than across the turn - over a
  // 60 s turn a turn-proportional bar is a 4% sliver exactly when it starts to matter.
  const warningLeft = clamp(0, msToChange / HANDOVER_WARNING_MS, 1);

  const cycle = (turn: number) => turn % args.singersOnTrack;

  return {
    tradingLines,
    handingOver,
    handOverAtBreakEnd,
    progressPercent: warningLeft * 100,
    showSwapOnCurrentLine: !tradingLines && args.nextSectionStart !== null && args.nextSectionStart === nextChange,
    showSwapOnNextLine:
      !tradingLines && args.subsequentSectionStart !== null && args.subsequentSectionStart === nextChange,
    msToChange,
    currentSinger: cycle(passed.length),
    nextSinger: cycle(passed.length + 1),
  };
};
